import { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Box, Button, Flex, Text, ThemeUIStyleObject } from 'theme-ui';
import SpinnerBar from '~/components/Spinner/loader';
import FormInputWithLabel from '~/components/Generic/Form/InputWithLabel';
import { useTranslate } from '~/hooks/utils/use-translate';

type Props = {
  forwardSx?: ThemeUIStyleObject;
  onSubmit: (code: string) => Promise<void>;
  inputLabel: string;
  inputPlaceholder?: string;
  buttonText: string;
  disabled?: boolean;
  getErrorMessage?: (error: string) => string;
};

interface FormInputs {
  code: string;
}

const RedemptionForm = ({
  forwardSx,
  onSubmit,
  inputLabel,
  inputPlaceholder,
  buttonText,
  disabled = false,
  getErrorMessage,
}: Props) => {
  const translate = useTranslate();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>();

  useEffect(() => {
    if (apiError) {
      const timeout = setTimeout(() => setApiError(null), 3000);
      return () => clearTimeout(timeout);
    }
  }, [apiError]);

  const handleFormSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (!data.code) {
      setApiError(translate('validation.required_field'));
      return;
    }

    try {
      setIsFetching(true);
      setApiError(null);
      await onSubmit(data.code);
      reset();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setApiError(getErrorMessage ? getErrorMessage(errorMessage) : errorMessage);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Flex sx={{ flexDirection: 'column', transition: '.3s ease', ...forwardSx }}>
      <Box as="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Flex
          sx={{
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              width: '80%',
              marginRight: '1rem',
            }}
          >
            <FormInputWithLabel
              forwardSx={{
                input: {
                  width: '100%',
                  height: '4rem',
                  border: '1px white solid',
                  fontSize: '1.6rem',
                  borderRadius: '12.4rem',
                  px: '2rem',
                  lineHeight: '10',
                  '::placeholder': {
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '1.3rem',
                    verticalAlign: 'middle',
                    letterSpacing: '0.02em',
                  },
                },
              }}
              label={inputLabel}
              aria-label={inputLabel}
              placeholder={inputPlaceholder || inputLabel}
              disabled={disabled || isFetching}
              {...register('code')}
            />
          </Box>
          <Button
            variant="knockout"
            sx={{
              height: '3.6rem',
              width: '8.2rem',
              fontSize: '1.2rem',
              textTransform: 'uppercase',
            }}
          >
            {isFetching && <SpinnerBar color="inherit" />}
            {!isFetching && <Text>{buttonText}</Text>}
          </Button>
        </Flex>
      </Box>

      <Text
        variant="formError"
        as="p"
        sx={{
          maxHeight: apiError ? '-moz-fit-content' : 0,
          color: 'white',
          backgroundColor: 'errorLight',
          textTransform: 'none',
          fontSize: '1.1rem',
          lineHeight: '1',
          opacity: apiError ? 1 : 0,
          transform: apiError ? 'scale(1)' : 'scale(0)',
          pt: apiError ? '12px' : 0,
          transition: 'all .3s',
        }}
      >
        {apiError}
      </Text>
      <Text
        variant="formError"
        as="p"
        sx={{
          maxHeight: errors?.code ? '-moz-fit-content' : 0,
          color: 'white',
          backgroundColor: 'errorLight',
          textTransform: 'none',
          fontSize: '1.1rem',
          lineHeight: '1',
          opacity: errors?.code ? 1 : 0,
          transform: errors?.code ? 'scale(1)' : 'scale(0)',
          pt: errors?.code ? '12px' : 0,
          transition: 'all .3s',
        }}
      >
        {errors?.code?.message}
      </Text>
    </Flex>
  );
};

export default RedemptionForm;
